import { FC, ReactNode } from 'react';
import {
  Badge,
  Box,
  Button,
  Paper,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { Mode } from '../types';
import { styled as muiStyled } from '@mui/material/styles';
import { DeleteOutline, Publish } from '@mui/icons-material';

const BoxContainer = muiStyled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(0),
  marginBottom: 24,
  marginTop: 8,
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
}));

interface DraftPublishSwitcherProps {
  haveUnpublishedChanges: boolean;
  onChange: (mode: Mode) => void;
  onDiscard: () => void;
  onPublish: () => void;
  isPublishedMode: boolean;
  hasPublishedVersion: boolean;
  previewAction?: ReactNode;
  summaryContent?: ReactNode;
  primaryAction?: ReactNode;
}

const DraftPublishSwitcher: FC<DraftPublishSwitcherProps> = ({
  onChange,
  onDiscard,
  onPublish,
  haveUnpublishedChanges,
  isPublishedMode,
  hasPublishedVersion,
  previewAction,
  summaryContent,
  primaryAction,
}) => {
  return (
    <BoxContainer elevation={0}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        gap={2}
        sx={{ px: { xs: 2, md: 3 }, py: 2.25 }}
      >
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
          {isPublishedMode ? (
            <Chip
              label="Published mode"
              size="small"
              sx={{ bgcolor: '#d1fae5', color: '#047857', fontWeight: 700 }}
            />
          ) : (
            <Chip
              label="Draft mode"
              size="small"
              sx={{ bgcolor: '#ffedd5', color: '#c2410c', fontWeight: 700 }}
            />
          )}
          <Box
            aria-hidden
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#cbd5e1',
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: '#64748b', fontWeight: 500 }}
          >
            {haveUnpublishedChanges ? 'Unsaved changes' : 'All changes saved'}
          </Typography>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="stretch"
          spacing={1.25}
          sx={{ width: { xs: '100%', md: 'auto' } }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{
              flex: { xs: 1, sm: 'initial' },
              '& > *': { flex: { xs: 1, sm: 'initial' } },
              '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' } },
            }}
          >
            {!isPublishedMode && hasPublishedVersion && (
              <Button
                variant="outlined"
                onClick={() => onChange(Mode.Published)}
                size="small"
                sx={{
                  height: 40,
                  px: { xs: 1.25, sm: 2.5 },
                  borderColor: '#dbe3ee',
                  color: '#2563eb',
                  bgcolor: '#f8fbff',
                  whiteSpace: 'nowrap',
                  fontSize: { xs: 12.5, sm: 14 },
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#eff6ff', borderColor: '#bfdbfe' },
                }}
              >
                View published
              </Button>
            )}
            {isPublishedMode && (
              <Badge
                color="secondary"
                badgeContent={haveUnpublishedChanges ? 1 : 0}
              >
                <Button
                  variant="outlined"
                  onClick={() => onChange(Mode.Draft)}
                  size="small"
                  sx={{
                    height: 40,
                    px: { xs: 1.25, sm: 2.5 },
                    borderColor: '#dbe3ee',
                    color: '#334155',
                    whiteSpace: 'nowrap',
                    fontSize: { xs: 12.5, sm: 14 },
                    textTransform: 'none',
                    borderRadius: 2,
                  }}
                >
                  View draft
                </Button>
              </Badge>
            )}
            {previewAction}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={0.75}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {!isPublishedMode && (
              <Button
                onClick={onDiscard}
                variant="outlined"
                startIcon={<DeleteOutline />}
                sx={{
                  px: { xs: 1.5, sm: 2.5 },
                  height: 40,
                  flex: { xs: 1, sm: 'initial' },
                  bgcolor: '#fffafa',
                  borderColor: '#fecaca',
                  color: '#dc2626',
                  borderRadius: 2,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#fff1f2',
                    borderColor: '#fda4af',
                  },
                }}
              >
                Discard draft
              </Button>
            )}
            {!isPublishedMode && (
              <Button
                onClick={() => {
                  onPublish();
                  onChange(Mode.Published);
                }}
                color="success"
                variant="contained"
                startIcon={<Publish />}
                sx={{
                  px: { xs: 1.5, sm: 3 },
                  height: 40,
                  flex: { xs: 1, sm: 'initial' },
                  bgcolor: '#00875f',
                  borderRadius: 2,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  boxShadow: '0 2px 5px rgba(0, 135, 95, 0.22)',
                  '&:hover': { bgcolor: '#007653' },
                }}
              >
                {haveUnpublishedChanges ? 'Save & ' : ''}Publish
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Box
        sx={{ borderTop: '1px solid #e2e8f0', px: { xs: 2, md: 3 }, py: 1.5 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          {summaryContent}
          {primaryAction}
        </Stack>
      </Box>
    </BoxContainer>
  );
};

export default DraftPublishSwitcher;
